import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import api from '../lib/api';
import FeatureHeader from './FeatureHeader';

export default function LinkedDevices({ onBack }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    deviceName: '',
    deviceType: 'smartwatch',
    identifier: '',
  });

  const deviceTypeOptions = useMemo(
    () => [
      { value: 'smartwatch', label: 'Smartwatch' },
      { value: 'fitness-band', label: 'Fitness Band' },
      { value: 'phone', label: 'Phone' },
      { value: 'glucose-monitor', label: 'Glucose Monitor' },
      { value: 'blood-pressure-monitor', label: 'Blood Pressure Monitor' },
      { value: 'other', label: 'Other' },
    ],
    []
  );

  const fetchDevices = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/linked-devices');
      setDevices(res.data?.devices || []);
    } catch (err) {
      setError('Failed to load linked devices.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevices();
  }, []);

  const handleAddDevice = async (e) => {
    e.preventDefault();
    const name = form.deviceName.trim();
    if (!name) {
      setError('Device name is required.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const res = await api.post('/api/linked-devices', {
        deviceName: name,
        deviceType: form.deviceType,
        identifier: form.identifier.trim(),
      });

      setDevices((prev) => [res.data.device, ...prev]);
      setForm({ deviceName: '', deviceType: 'smartwatch', identifier: '' });
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to link device.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDisconnect = async (id) => {
    setError('');
    try {
      await api.delete(`/api/linked-devices/${id}`);
      setDevices((prev) => prev.filter((d) => d._id !== id));
    } catch (err) {
      setError('Failed to disconnect device.');
    }
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return '-';
    const d = new Date(dateValue);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString();
  };

  return (
    <div style={pageStyle}>
      <FeatureHeader title="Linked Devices" onBack={onBack} />

      <form onSubmit={handleAddDevice} style={formCardStyle}>
        <div style={formGridStyle}>
          <div>
            <label style={labelStyle}>Device Name</label>
            <input
              value={form.deviceName}
              onChange={(e) => setForm((prev) => ({ ...prev, deviceName: e.target.value }))}
              placeholder="e.g. Apple Watch Series 8"
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>Device Type</label>
            <select
              value={form.deviceType}
              onChange={(e) => setForm((prev) => ({ ...prev, deviceType: e.target.value }))}
              style={inputStyle}
            >
              {deviceTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>Identifier (Optional)</label>
            <input
              value={form.identifier}
              onChange={(e) => setForm((prev) => ({ ...prev, identifier: e.target.value }))}
              placeholder="Device ID or serial"
              style={inputStyle}
            />
          </div>
        </div>

        <button type="submit" style={addBtnStyle} disabled={submitting}>
          {submitting ? 'Linking...' : '+ Link Device'}
        </button>
      </form>

      {error && <div style={errorStyle}>{error}</div>}

      <div style={tableWrapStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Device</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Identifier</th>
              <th style={thStyle}>Linked On</th>
              <th style={thStyle}>Last Synced</th>
              <th style={thStyle}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td style={tdStyle} colSpan={6}>Loading devices...</td>
              </tr>
            ) : devices.length === 0 ? (
              <tr>
                <td style={tdStyle} colSpan={6}>No linked devices yet.</td>
              </tr>
            ) : (
              devices.map((device) => (
                <tr key={device._id}>
                  <td style={tdStyle}>{device.deviceName}</td>
                  <td style={tdStyle}>{device.deviceType || 'other'}</td>
                  <td style={tdStyle}>{device.identifier || '-'}</td>
                  <td style={tdStyle}>{formatDate(device.createdAt)}</td>
                  <td style={tdStyle}>{formatDate(device.lastSyncedAt)}</td>
                  <td style={tdStyle}>
                    <button
                      style={disconnectBtnStyle}
                      onClick={() => handleDisconnect(device._id)}
                    >
                      Disconnect
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh',
  padding: '24px 16px',
  background: 'var(--bg)',
  fontFamily: 'sans-serif',
  color: 'var(--on-bg)',
};

const headerRowStyle = {
  display: 'flex',
  alignItems: 'center',
  marginBottom: 16,
};

const backBtnStyle = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  marginRight: 8,
  cursor: 'pointer',
};

const headerStyle = {
  fontSize: '1.25rem',
  fontWeight: '700',
};

const formCardStyle = {
  background: 'var(--surface)',
  borderRadius: 12,
  padding: 16,
  marginBottom: 12,
  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
};

const formGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 10,
  marginBottom: 12,
};

const labelStyle = {
  fontSize: '0.78rem',
  fontWeight: 600,
  marginBottom: 4,
  display: 'block',
};

const inputStyle = {
  width: '100%',
  border: '1px solid #d0d5dd',
  borderRadius: 8,
  padding: '8px 10px',
  fontSize: '0.86rem',
};

const addBtnStyle = {
  padding: '10px 16px',
  background: 'var(--primary)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: '0.9rem',
  fontWeight: '500',
  cursor: 'pointer',
};

const errorStyle = {
  marginBottom: 10,
  color: '#b42318',
  fontSize: '0.85rem',
};

const tableWrapStyle = {
  overflowX: 'auto',
  background: 'var(--surface)',
  borderRadius: 12,
  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  minWidth: 760,
};

const thStyle = {
  textAlign: 'left',
  fontSize: '0.78rem',
  fontWeight: 700,
  color: '#475467',
  padding: '10px 12px',
  borderBottom: '1px solid #eaecf0',
  background: '#f8fafc',
};

const tdStyle = {
  fontSize: '0.84rem',
  color: '#111827',
  padding: '10px 12px',
  borderBottom: '1px solid #f1f5f9',
};

const disconnectBtnStyle = {
  padding: '6px 10px',
  fontSize: '0.75rem',
  background: '#eee',
  borderRadius: 8,
  border: 'none',
  color: '#333',
  cursor: 'pointer',
};
